import clsx from 'clsx';
import Button from 'components/common/Button';
import { FadeIn } from 'components/common/FadeIn';
import InputWithButton from 'components/common/InputWithButton';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { trackEvent } from 'utils/analytics/analytics';
import { formattedPriceStringFromPence } from 'utils/common';
import { prepareForTransition } from 'utils/dom';
import Chevron from '../svg/24x24/ChevronRight.svg';
import Lock from '../svg/EnterFlowInfoGraphic/lock.svg';
import Star from '../svg/EnterFlowInfoGraphic/star.svg';
import Verified from '../svg/EnterFlowInfoGraphic/verified.svg';
import ChooseYourNumbers from '../svg/Mascots/choose-your-numbers.svg';
import MinusRing from '../svg/MinusRing.svg';
import PlusRing from '../svg/PlusRing.svg';
import Tick from '../svg/Tick.svg';
import TrustpilotLogo from '../svg/Trustpilot/trustpilot-green-hero.svg';
import styles from './MembershipSummary.module.css';

export const InfoGraphicRow = () => {
  return (
    <div className={styles.infoGraphicContainer}>
      <div className={styles.infoGraphic}>
        <Star />
        <div className="daymadeTypographySRegular">Rated Excellent</div>
      </div>
      <div className={styles.infoGraphic}>
        <Lock />
        <div className="daymadeTypographySRegular">Secure Checkout</div>
      </div>
      <div className={styles.infoGraphic}>
        <Verified />
        <div className="daymadeTypographySRegular">Cancel anytime</div>
      </div>
    </div>
  );
};

interface MembershipTotalProps {
  checkoutPage?: boolean;
  onLightBackground?: boolean;
  showMascot?: boolean;
  hideSummaryItemsByDefault?: boolean;
}

const MembershipTotal = observer(
  ({
    checkoutPage,
    onLightBackground,
    showMascot,
    hideSummaryItemsByDefault,
  }: MembershipTotalProps) => {
    const {
      days,
      lines,
      orderCost,
      discountThisWeekText,
      isReferralFlow,
      referralDetails,
      applyPromo: applyPromoCode,
      promoErrorMessage,
      promoCode,
    } = useEnterStore();
    const { isMobile } = useIsMobile();
    const router = useRouter();

    const summaryItemsRef = useRef<HTMLDivElement>(null);
    const isTransitioning = useRef(false);
    const [promoCodeOpen, setPromoCodeOpen] = useState(false);
    const [promoCodeAlreadySet, setPromoCodeAlreadySet] = useState(false);
    const [promoCodeState, setPromoCodeState] = useState<string>(
      promoCode === 'FIRSTORDER50OFF' ? '' : promoCode ? promoCode : '',
    );
    const [applyPromoCodeLoading, setApplyPromoCodeLoading] = useState(false);
    const [showSummaryItems, setShowSummaryItems] = useState(
      hideSummaryItemsByDefault ? false : true,
    );
    const [summaryItemsHeight, setSummaryItemsHeight] = useState<
      number | undefined
    >(undefined);

    const drawDays =
      days.length > 1
        ? 'Tuesday & Friday'
        : days[0] === 'FRIDAY'
          ? 'Friday'
          : 'Tuesday';

    const isDiscounted = orderCost.price !== orderCost.cost;

    useEffect(() => {
      if (summaryItemsHeight) return;

      const ref = summaryItemsRef.current;
      if (ref) {
        setSummaryItemsHeight(ref.offsetHeight);
      }
    }, [summaryItemsRef.current]);

    useEffect(() => {
      if (promoCode && promoCode !== 'FIRSTORDER50OFF') {
        setPromoCodeState(promoCode);
        setPromoCodeAlreadySet(true);
      }
    }, [promoCode]);

    useEffect(() => {
      if (checkoutPage && onLightBackground && isMobile) {
        setShowSummaryItems(false);
      }
    }, [checkoutPage, onLightBackground, isMobile]);

    const submitPromoCode = async (
      e: FormEvent<HTMLFormElement>,
      promoCodeStr: string,
    ) => {
      e.preventDefault();
      setApplyPromoCodeLoading(true);
      await applyPromoCode(promoCodeStr);
      setApplyPromoCodeLoading(false);
      trackEvent('Set Promo Code', { promoCodeStr });
    };

    const toggleSummaryItems = (toggleChange: boolean) => {
      const ref = summaryItemsRef.current;
      if (toggleChange) {
        if (ref) {
          ref.style.height = `${summaryItemsHeight}px`;
        }
      } else {
        if (ref) {
          ref.style.height = `0px`;
        }
      }
      setShowSummaryItems(toggleChange);
    };

    return (
      <>
        {showMascot && (
          <div className={styles.mascot}>
            <ChooseYourNumbers />
          </div>
        )}
        <div
          className={clsx(
            styles.container,
            onLightBackground && styles.blueBackground,
          )}
        >
          <div className={styles.header}>
            <div className="daymadeTypographyMBlack">Membership Total</div>
            <div className={styles.headerRight}>
              {discountThisWeekText && isDiscounted && (
                <div className={styles.discountMessageContainer}>
                  <div
                    className={clsx(
                      styles.discountMessage,
                      'daymadeTypographyXS',
                    )}
                  >
                    {discountThisWeekText}
                  </div>
                  {isReferralFlow && (
                    <div
                      className={clsx(
                        styles.discountMessageReferral,
                        'daymadeTypographyXS',
                      )}
                    >
                      win with {referralDetails?.firstName || 'friends'}
                    </div>
                  )}
                </div>
              )}
              {checkoutPage && onLightBackground && (
                <button
                  onClick={() => {
                    if (isTransitioning.current) return;
                    isTransitioning.current = true;

                    if (!showSummaryItems) {
                      const summaryItems = document.querySelector(
                        '[data-summary-items-element]',
                      ) as HTMLElement | undefined;

                      if (summaryItems) {
                        const scrollHeight = summaryItems?.scrollHeight;

                        const transitionEnd = () => {
                          summaryItems.style.height = '';
                          summaryItems.removeEventListener(
                            'transitionend',
                            transitionEnd,
                          );
                          isTransitioning.current = false;
                        };
                        summaryItems.addEventListener(
                          'transitionend',
                          transitionEnd,
                        );

                        // The addition of 32 is the padding on the component
                        setSummaryItemsHeight(scrollHeight + 32);
                      } else {
                        setSummaryItemsHeight(0);
                      }
                    } else {
                      const scrollHeight = document.querySelector(
                        '[data-summary-items-element]',
                      )?.scrollHeight;

                      if (scrollHeight && summaryItemsRef.current) {
                        const summary = summaryItemsRef.current;
                        summary.style.height = scrollHeight + 'px';

                        prepareForTransition(summaryItemsRef.current);
                        const transitionEnd = () => {
                          isTransitioning.current = false;
                          summary.removeEventListener(
                            'transitionend',
                            transitionEnd,
                          );
                        };
                        summary.addEventListener(
                          'transitionend',
                          transitionEnd,
                        );
                      }

                      setSummaryItemsHeight(0);
                    }
                    setShowSummaryItems(!showSummaryItems);
                  }}
                  className={clsx(
                    styles.showSummaryItemsToggle,
                    showSummaryItems && styles.reverseChevron,
                    'buttonReset',
                  )}
                >
                  <Chevron />
                </button>
              )}
            </div>
          </div>
          <div
            className={clsx(
              styles.summaryItems,
              showSummaryItems
                ? styles.showSummaryItems
                : styles.hideSummaryItems,
            )}
            data-summary-items-element
            ref={summaryItemsRef}
            style={{ height: summaryItemsHeight }}
          >
            {checkoutPage && (
              <div className={styles.summaryItem}>
                <div className={styles.tickWrapper}>
                  <Tick />
                </div>
                <div className="daymadeTypographySRegular">
                  Your lucky numbers
                </div>
              </div>
            )}
            {lines.length > 1 && (
              <div className={styles.summaryItem}>
                <div className={styles.tickWrapper}>
                  <Tick />
                </div>
                <div className="daymadeTypographySRegular">
                  {lines.length} entries per draw
                </div>
              </div>
            )}
            <div className={styles.summaryItem}>
              <div className={styles.tickWrapper}>
                <Tick />
              </div>
              <div className="daymadeTypographySRegular">
                A tree planted with every draw
              </div>
            </div>
            <div className={styles.summaryItem}>
              <div className={styles.tickWrapper}>
                <Tick />
              </div>
              <div className="daymadeTypographySRegular">
                Draw day{days.length > 1 && 's'}: {drawDays}
              </div>
            </div>
          </div>
          <div
            className={clsx('daymadeTypographySRegular', styles.costSummary)}
          >
            <div className={styles.costHeadline}>
              <span
                className={clsx(
                  'daymadeTypographyLBold',
                  styles.price,
                  isDiscounted && styles.discountColour,
                )}
              >
                {formattedPriceStringFromPence(orderCost.cost)}
              </span>{' '}
              today
            </div>
            <div className={styles.discountHeadline}>
              {isDiscounted && (
                <>
                  Then {formattedPriceStringFromPence(orderCost.price)} weekly
                </>
              )}
            </div>
          </div>
          {checkoutPage && (
            <div className={styles.promoSection}>
              <div
                className={clsx(
                  'daymadeTypographySRegular',
                  styles.promoActions,
                )}
              >
                <button className="buttonReset" onClick={() => router.back()}>
                  Edit
                </button>
                <button
                  className={clsx(styles.promoCodeButton, 'buttonReset')}
                  onClick={() => setPromoCodeOpen(!promoCodeOpen)}
                >
                  <span className={styles.promoCodeText}>Promo Code</span>
                  <span className={styles.promoCodeIcon}>
                    {promoCodeOpen ? <MinusRing /> : <PlusRing />}
                  </span>
                </button>
              </div>
              <form
                className={clsx(
                  styles.formPromoCode,
                  promoCodeOpen ? styles.showPromoForm : styles.hidePromoForm,
                  promoCodeAlreadySet && styles.promoCodeIsSet,
                )}
                onSubmit={(e) =>
                  promoCodeState ? submitPromoCode(e, promoCodeState) : {}
                }
              >
                <InputWithButton
                  inputPlaceholder="Enter your promo code here"
                  inputValue={promoCodeState}
                  styles2026
                  inputColor="transparent"
                  buttonText="Apply"
                  buttonSize2026="Medium"
                  onChangeInput={(e) => {
                    setPromoCodeState(e.target.value);
                  }}
                  onFocusInput={() => setPromoCodeAlreadySet(false)}
                  disabled={!(promoCodeState && promoCodeState?.length >= 3)}
                  loading={applyPromoCodeLoading}
                />
                {promoCodeAlreadySet && <Tick />}
              </form>
              {promoCodeOpen && promoErrorMessage && (
                <div
                  className={clsx(
                    styles.errorMessage,
                    'daymadeTypographySRegular',
                  )}
                >
                  {promoErrorMessage}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  },
);

interface MembershipSummaryProps {
  checkoutPage?: boolean;
  hideSummaryItemsByDefault?: boolean;
  notifyMissingNumbers?: () => void;
  onLightBackground?: boolean;
  showMascot?: boolean;
}

const MembershipSummary = observer(
  ({
    checkoutPage,
    hideSummaryItemsByDefault,
    notifyMissingNumbers,
    onLightBackground,
    showMascot,
  }: MembershipSummaryProps) => {
    const Router = useRouter();
    const { isMobile } = useIsMobile();
    const { linesValid } = useEnterStore();

    return (
      <div className={styles.membershipSummary}>
        <FadeIn onScroll>
          <MembershipTotal
            checkoutPage={checkoutPage}
            onLightBackground={onLightBackground}
            showMascot={showMascot}
            hideSummaryItemsByDefault={hideSummaryItemsByDefault}
          />
        </FadeIn>
        {!checkoutPage && (
          <FadeIn onScroll delay={0.1}>
            <div className={styles.continueButton}>
              <Button
                buttonSize="Large"
                fullWidth
                looksDisabled={!linesValid}
                onClick={(e) => {
                  e?.preventDefault();

                  if (!linesValid) {
                    notifyMissingNumbers && notifyMissingNumbers();
                  } else {
                    Router.push('/checkout');
                  }
                }}
              >
                Continue
              </Button>
            </div>
          </FadeIn>
        )}
        {isMobile && checkoutPage ? null : (
          <FadeIn onScroll delay={0.2}>
            <InfoGraphicRow />
          </FadeIn>
        )}
        {isMobile && !checkoutPage && (
          <FadeIn onScroll delay={0.2} className={styles.trustpilot}>
            <TrustpilotLogo />
          </FadeIn>
        )}
      </div>
    );
  },
);

export default MembershipSummary;
